import React, {Fragment, useState} from "react";
import {css, Theme} from "@emotion/react";

import {Badge} from "@pg-design/badge-module";
import {calculateRemSize, display, m, position} from "@pg-design/helpers-css";
import {PercentIcon, UnlockIcon} from "@pg-design/icons-module";
import {Highlight} from "@pg-design/text-module";
import {useABTest} from "@pg-mono/ab-testing";
import {rpAppLink} from "@pg-mono/rp-routes";
import {areaFormat, pluralize} from "@pg-mono/string-utils";
import {useUserDevice} from "@pg-mono/user-device";

import {abTestingInstance} from "../../ab_testing/ab_testing_instance";
import {OngoingABTests} from "../../ab_testing/ongoing_ab_tests";
import {DetailViewHeader} from "../../atoms/DetailViewHeader";
import {LinkWithLabel} from "../../atoms/LinkWithLabel";
import {PropertyBooster} from "../../boosters/components/PropertyBooster";
import {IBoosterClickHandlerParams} from "../../boosters/types/IBoosterClickHandlerParams";
import {DetailHeaderDropdownMenu} from "../../offer/components/DetailHeaderDropdownMenu";
import {useOfferLink} from "../../offer/detail/hooks/use_offer_link";
import {getOfferTypeNameCapital} from "../../offer/helpers/OfferType";
import {HeaderCta} from "../../real_estate/components/HeaderCta";
import {SubscribeButton} from "../../real_estate/components/SubscribeButton";
import {IPropertyDetailPromotion} from "../actions/fetch_property_detail_promotions";
import {IPropertyDetail} from "../types/IPropertyDetail";
import {PropertyDisplayType} from "../types/PropertyDisplayType";
import {isPropertyFavouriteButtonAvailable} from "../utils/is_property_favourite_button_available";
import {isPropertyForSale} from "../utils/is_property_for_sale";
import {PropertyDetailHeaderPrice} from "./PropertyDetailHeaderPrice";

interface IProps {
    property: IPropertyDetail;
    promotions: IPropertyDetailPromotion[] | null;
    onBoosterClick: (params: IBoosterClickHandlerParams) => void;
    onLocationClick: () => void;
}

const roomPlural = pluralize(["pokój", "pokoje", "pokoi"]);

const boosterABTestProps = abTestingInstance.getABTestContainerProps(OngoingABTests.BOOSTER);

export const PropertyDetailHeader = (props: IProps) => {
    const {property, promotions} = props;
    const {isMobile} = useUserDevice();
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);

    const boosterTestProps = useABTest(boosterABTestProps);

    const handleDropdownToggle = () => {
        setIsDropdownOpen((prevState) => !prevState);
    };
    const isApartment = property.offer.is_holiday_location || property.offer.is_condohotel;
    const isPublished = property.configuration.display_type === PropertyDisplayType.PUBLISHED;

    const offerLink = useOfferLink(property.offer);
    const vendorLink = rpAppLink.vendor.detail.base({vendorSlug: property.offer.vendor.slug, vendorId: property.offer.vendor.id});

    const isPriceButtonAvailable = isPropertyForSale(property);
    const isFavouriteButtonAvailable = isPropertyFavouriteButtonAvailable(property);

    const boosterStyle = isDropdownOpen ? boosterMoved : boosterPosition;

    return (
        <div css={wrapper}>
            <DetailViewHeader
                address={property.offer.address}
                badges={
                    <Fragment>
                        {property.discount && (
                            <Badge variant="label_success" icon={PercentIcon}>
                                Przecena {property.discount}%
                            </Badge>
                        )}

                        {property.is_reserved && (
                            <Badge variant="label_warning" icon={UnlockIcon}>
                                Rezerwacja
                            </Badge>
                        )}

                        {(!property.for_sale || !isPublished) && <Badge variant="label_secondary">Oferta archiwalna</Badge>}

                        {/* Due to CU-8693ecg7r temporary commented - program "Kredyt 2%" has ended at the begging of 2024 */}
                        {/*{property.credit_two_percent && isPublished && (*/}
                        {/*    <Badge variant="label_highlight" icon={MegaphoneIcon}>*/}
                        {/*        Kredyt 2%*/}
                        {/*    </Badge>*/}
                        {/*)}*/}
                    </Fragment>
                }
                cta={
                    <HeaderCta>
                        {!isMobile && isPriceButtonAvailable && <PropertyDetailHeaderPrice property={property} />}
                        {!isMobile && isFavouriteButtonAvailable && <SubscribeButton offerId={property.offer.id} propertyId={property.id} size="l" />}
                        <DetailHeaderDropdownMenu onToggle={handleDropdownToggle} />
                        {boosterTestProps.canABTestBeRendered && property.for_sale && (
                            <PropertyBooster onBoosterClick={props.onBoosterClick} property={property} promotions={promotions} css={boosterStyle} />
                        )}
                    </HeaderCta>
                }
                externalLinks={
                    <h2 css={m(0)}>
                        <LinkWithLabel href={offerLink} label="Inwestycja:" linkText={property.offer.name} />
                        <LinkWithLabel href={vendorLink} label="Deweloper:" linkText={property.offer.vendor.name} />
                    </h2>
                }
                onLocationClick={props.onLocationClick}
                title={
                    <Fragment>
                        {isApartment ? "Apartament" : getOfferTypeNameCapital(property.offer.type)} na sprzedaż{" "}
                        <Highlight>
                            {property.rooms} {roomPlural(property.rooms)} o powierzchni {areaFormat(property.area, {precision: 2})} - numer {property.number}
                        </Highlight>
                    </Fragment>
                }
            />
        </div>
    );
};

// Styles;
const boosterMoved = css`
    transform: translate(0, ${calculateRemSize(16)});
    border-radius: ${calculateRemSize(2)};
`;

const boosterPosition = css`
    margin-top: ${calculateRemSize(5)};
    border-radius: ${calculateRemSize(2)};
`;

const wrapper = (theme: Theme) => css`
    ${display("flex")};
    ${position("relative")};
    flex-wrap: wrap;

    @media (min-width: ${theme.breakpoints.md}) {
        flex-wrap: nowrap;
    }
`;
import React, {Fragment, useState} from "react";
import {css, Theme} from "@emotion/react";

import {Badge} from "@pg-design/badge-module";
import {calculateRemSize, display, m, position} from "@pg-design/helpers-css";
import {PercentIcon, UnlockIcon} from "@pg-design/icons-module";
import {Highlight} from "@pg-design/text-module";
import {useABTest} from "@pg-mono/ab-testing";
import {rpAppLink} from "@pg-mono/rp-routes";
import {areaFormat, pluralize} from "@pg-mono/string-utils";
import {useUserDevice} from "@pg-mono/user-device";

import {abTestingInstance} from "../../ab_testing/ab_testing_instance";
import {OngoingABTests} from "../../ab_testing/ongoing_ab_tests";
import {DetailViewHeader} from "../../atoms/DetailViewHeader";
import {LinkWithLabel} from "../../atoms/LinkWithLabel";
import {PropertyBooster} from "../../boosters/components/PropertyBooster";
import {IBoosterClickHandlerParams} from "../../boosters/types/IBoosterClickHandlerParams";
import {DetailHeaderDropdownMenu} from "../../offer/components/DetailHeaderDropdownMenu";
import {useOfferLink} from "../../offer/detail/hooks/use_offer_link";
import {getOfferTypeNameCapital} from "../../offer/helpers/OfferType";
import {HeaderCta} from "../../real_estate/components/HeaderCta";
import {SubscribeButton} from "../../real_estate/components/SubscribeButton";
import {IPropertyDetailPromotion} from "../actions/fetch_property_detail_promotions";
import {IPropertyDetail} from "../types/IPropertyDetail";
import {PropertyDisplayType} from "../types/PropertyDisplayType";
import {isPropertyFavouriteButtonAvailable} from "../utils/is_property_favourite_button_available";
import {isPropertyForSale} from "../utils/is_property_for_sale";
import {PropertyDetailHeaderPrice} from "./PropertyDetailHeaderPrice";

interface IProps {
    property: IPropertyDetail;
    promotions: IPropertyDetailPromotion[] | null;
    onBoosterClick: (params: IBoosterClickHandlerParams) => void;
    onLocationClick: () => void;
}

const roomPlural = pluralize(["pokój", "pokoje", "pokoi"]);

const boosterABTestProps = abTestingInstance.getABTestContainerProps(OngoingABTests.BOOSTER);

export const PropertyDetailHeader = (props: IProps) => {
    const {property, promotions} = props;
    const {isMobile} = useUserDevice();
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);

    const boosterTestProps = useABTest(boosterABTestProps);

    const handleDropdownToggle = () => {
        setIsDropdownOpen((prevState) => !prevState);
    };
    const isApartment = property.offer.is_holiday_location || property.offer.is_condohotel;
    const isPublished = property.configuration.display_type === PropertyDisplayType.PUBLISHED;

    const offerLink = useOfferLink(property.offer);
    const vendorLink = rpAppLink.vendor.detail.base({vendorSlug: property.offer.vendor.slug, vendorId: property.offer.vendor.id});

    const isPriceButtonAvailable = isPropertyForSale(property);
    const isFavouriteButtonAvailable = isPropertyFavouriteButtonAvailable(property);

    const boosterStyle = isDropdownOpen ? boosterMoved : boosterPosition;

    return (
        <div css={wrapper}>
            <DetailViewHeader
                address={property.offer.address}
                badges={
                    <Fragment>
                        {property.discount && (
                            <Badge variant="label_success" icon={PercentIcon}>
                                Przecena {property.discount}%
                            </Badge>
                        )}

                        {property.is_reserved && (
                            <Badge variant="label_warning" icon={UnlockIcon}>
                                Rezerwacja
                            </Badge>
                        )}

                        {(!property.for_sale || !isPublished) && <Badge variant="label_secondary">Oferta archiwalna</Badge>}

                        {/* Due to CU-8693ecg7r temporary commented - program "Kredyt 2%" has ended at the begging of 2024 */}
                        {/*{property.credit_two_percent && isPublished && (*/}
                        {/*    <Badge variant="label_highlight" icon={MegaphoneIcon}>*/}
                        {/*        Kredyt 2%*/}
                        {/*    </Badge>*/}
                        {/*)}*/}
                    </Fragment>
                }
                cta={
                    <HeaderCta>
                        {!isMobile && isPriceButtonAvailable && <PropertyDetailHeaderPrice property={property} />}
                        {!isMobile && isFavouriteButtonAvailable && <SubscribeButton offerId={property.offer.id} propertyId={property.id} size="l" />}
                        <DetailHeaderDropdownMenu onToggle={handleDropdownToggle} />
                        {boosterTestProps.canABTestBeRendered && property.for_sale && (
                            <PropertyBooster onBoosterClick={props.onBoosterClick} property={property} promotions={promotions} css={boosterStyle} />
                        )}
                    </HeaderCta>
                }
                externalLinks={
                    <h2 css={m(0)}>
                        <LinkWithLabel href={offerLink} label="Inwestycja:" linkText={property.offer.name} />
                        <LinkWithLabel href={vendorLink} label="Deweloper:" linkText={property.offer.vendor.name} />
                    </h2>
                }
                onLocationClick={props.onLocationClick}
                title={
                    <Fragment>
                        {isApartment ? "Apartament" : getOfferTypeNameCapital(property.offer.type)} na sprzedaż{" "}
                        <Highlight>
                            {property.rooms} {roomPlural(property.rooms)} o powierzchni {areaFormat(property.area, {precision: 2})} - numer {property.number}
                        </Highlight>
                    </Fragment>
                }
            />
        </div>
    );
};

// Styles;
const boosterMoved = css`
    transform: translate(0, ${calculateRemSize(16)});
    border-radius: ${calculateRemSize(2)};
`;

const boosterPosition = css`
    margin-top: ${calculateRemSize(5)};
    border-radius: ${calculateRemSize(2)};
`;

const wrapper = (theme: Theme) => css`
    ${display("flex")};
    ${position("relative")};
    flex-wrap: wrap;

    @media (min-width: ${theme.breakpoints.md}) {
        flex-wrap: nowrap;
    }
`;
